import { CrowdctrlListComponent } from '../list/list.component';
import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { OrderService } from '../../services/order.service';
import { extractErrorMessage } from '../../utils/error.utils';
import { LoadingButtonComponent } from '../loading-button/loading-button.component';
import { AlphabeticKeyboardComponent } from '../alphabetic-keyboard/alphabetic-keyboard.component';
import { NumericKeyboardComponent } from '../numeric-keyboard/numeric-keyboard.component';
import { QueueSelectionDisplayMode, SourceType } from '@crowdctrl/core';
import { Order, SessionQueue } from '@crowdctrl/core';
import {
  IonItem,
  IonLabel,
  IonRadio,
  IonRadioGroup,
  IonSelect,
  IonSelectOption,
} from '@ionic/angular/standalone';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'crowdctrl-queue-selection-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    LoadingButtonComponent,
    AlphabeticKeyboardComponent,
    NumericKeyboardComponent,
    IonItem,
    IonLabel,
    CrowdctrlListComponent,
    IonRadio,
    IonRadioGroup,
    IonSelect,
    IonSelectOption,
  ],
  templateUrl: './queue-selection-form.component.html',
  styleUrl: './queue-selection-form.component.css',
})
export class QueueSelectionFormComponent implements OnChanges {
  @Input() workspaceId!: string;
  @Input() sessionId!: string;
  @Input() queueControls: SessionQueue[] = [];
  @Input() defaultSelectedQueueId: string | null = null;
  @Input() inputMode: 'alphabetic' | 'numeric' = 'alphabetic';
  @Input() displayMode: QueueSelectionDisplayMode = 'auto';
  @Input() sourceType!: SourceType;
  @Input() sourceId?: string;
  @Input() workspaceName?: string | null;
  @Input() sessionName?: string | null;
  @Input() formName = 'Queue';
  @Input() nameLabel = 'Enter Your Name';
  @Input() idLabel = 'Enter Your ID';
  /** Primary action label (e.g. "Add to Queue" in agent, "Submit" on public short URL). */
  @Input() submitLabel = 'Place Order';
  /** Label on the same button while the spinner is shown (crowdctrl-loading-button loadingText). */
  @Input() submitLoadingLabel = 'Submitting';
  @Input() disabled = false;
  /**
   * When true, the primary submit button is disabled (e.g. Forms kiosk: staff unlocked the form for setup).
   */
  @Input() submitButtonDisabled = false;
  /**
   * When set, used instead of OrderService (e.g. anonymous public form intake via fetch).
   */
  @Input() createOrderFn?: (orderData: {
    queueId: string;
    summary: string;
    customerIdentifier: string;
    sourceType?: string;
    sourceId?: string;
    sourceMetadata?: Record<string, string>;
  }) => Promise<{ order: Order }>;

  @Output() orderCreated = new EventEmitter<{ orderId: string; order: Order }>();
  @Output() error = new EventEmitter<string>();

  selectedQueueId: string | null = null;
  customerName = '';
  customerId = '';
  isSubmitting = false;

  private orderService = inject(OrderService);

  /** Queues shown in select / radio list, ordered A-Z by display name (all hosts share this). */
  get sortedQueueControls(): SessionQueue[] {
    const list = this.queueControls ?? [];
    if (list.length <= 1) {
      return [...list];
    }
    return [...list].sort((a, b) =>
      (a.name || '').localeCompare(b.name || '', undefined, {
        sensitivity: 'base',
        numeric: true,
      }),
    );
  }

  get shouldShowSelect(): boolean {
    return this.displayMode === 'select' || this.autoSelectsDropdown;
  }

  get shouldShowRadio(): boolean {
    return (
      this.displayMode === 'radio' || (this.displayMode === 'auto' && !this.autoSelectsDropdown)
    );
  }

  get shouldShowTiles(): boolean {
    return this.displayMode === 'tiles';
  }

  private get autoSelectsDropdown(): boolean {
    return this.displayMode === 'auto' && this.sortedQueueControls.length > 3;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['queueControls'] || changes['defaultSelectedQueueId']) {
      this.applyDefaultSelectedQueue();
    }
  }

  private applyDefaultSelectedQueue(): void {
    if (this.selectedQueueId) return;
    if (!this.defaultSelectedQueueId) return;
    const hasDefault = this.queueControls.some((q) => q.id === this.defaultSelectedQueueId);
    if (hasDefault) {
      this.selectedQueueId = this.defaultSelectedQueueId;
    }
  }

  onQueueItemClick(queueId: string): void {
    this.selectedQueueId = queueId;
  }

  onNameChange(name: string): void {
    this.customerName = name;
  }

  onIdChange(id: string): void {
    this.customerId = id;
  }

  async onSubmit(): Promise<void> {
    if (this.isSubmitting || this.disabled || this.submitButtonDisabled) {
      return;
    }

    if (!this.selectedQueueId) {
      this.error.emit('Please select a queue');
      return;
    }

    if (this.inputMode === 'numeric') {
      if (!this.customerId || this.customerId.length < 1 || this.customerId.length > 12) {
        this.error.emit('Please enter a valid ID (1-12 digits)');
        return;
      }
    } else {
      if (!this.customerName || this.customerName.trim().length === 0) {
        this.error.emit('Please enter your name');
        return;
      }
    }

    if (!this.workspaceId || !this.sessionId) {
      this.error.emit('Invalid form information');
      return;
    }

    this.isSubmitting = true;

    try {
      const summary =
        this.inputMode === 'numeric'
          ? `Customer ID: ${this.customerId}`
          : `Customer: ${this.customerName}`;

      const customerIdentifier = this.inputMode === 'numeric' ? this.customerId : this.customerName;

      const orderData: {
        queueId: string;
        summary: string;
        customerIdentifier: string;
        sourceType?: string;
        sourceId?: string;
        sourceMetadata?: Record<string, string>;
      } = {
        queueId: this.selectedQueueId,
        summary,
        customerIdentifier,
      };

      if (this.sourceType) {
        orderData.sourceType = this.sourceType;
        if (this.sourceId) {
          orderData.sourceId = this.sourceId;
        }
      }

      const sourceMetadata: Record<string, string> = {};
      if (this.workspaceName?.trim()) {
        sourceMetadata['workspaceName'] = this.workspaceName.trim();
      }
      if (this.sessionName?.trim()) {
        sourceMetadata['sessionName'] = this.sessionName.trim();
      }
      if (Object.keys(sourceMetadata).length > 0) {
        orderData.sourceMetadata = sourceMetadata;
      }

      const result = this.createOrderFn
        ? await this.createOrderFn(orderData)
        : await firstValueFrom(
            this.orderService.createOrder(this.workspaceId, this.sessionId, orderData),
          );

      if (!result || !result.order) {
        throw new Error('Order creation failed: No order returned');
      }

      this.orderCreated.emit({ orderId: result.order.id, order: result.order });
    } catch (err) {
      console.error('Error creating order:', err);
      const errorMsg = extractErrorMessage(err) || 'Failed to create order. Please try again.';
      this.error.emit(errorMsg);
      this.isSubmitting = false;
    }
  }

  get isSubmitDisabled(): boolean {
    if (this.isSubmitting || this.disabled || this.submitButtonDisabled || !this.selectedQueueId) {
      return true;
    }
    if (this.inputMode === 'numeric') {
      return !this.customerId || this.customerId.length < 1 || this.customerId.length > 12;
    }
    return !this.customerName || this.customerName.trim().length === 0;
  }
}
